/**
 * @file shared.jsx
 * @brief Provides reusable stat, header, and pagination UI primitives for the catalog admin screens.
 */
import styles from "../catalog-admin.module.css";


/**
 * Render a compact dashboard stat card.
 * @param {string} label Card label.
 * @param {string|number} value Card value.
 * @param {string} tone Optional visual tone modifier.
 * @returns {JSX.Element} The stat card component.
 */
export function StatCard({ label, value, tone = "default" }) {
    return (
        <div className={`${styles.statCard} ${styles[`statCard${tone}`] || ""}`}>
            <span className={styles.statLabel}>{label}</span>
            <strong className={styles.statValue}>{value}</strong>
        </div>
    );
}


/**
 * Render a section header with optional actions area.
 * @param {string} icon Emoji or icon prefix shown before the title.
 * @param {string} title Section title.
 * @param {string} subtitle Optional section subtitle.
 * @param {React.ReactNode} actions Optional action controls.
 * @returns {JSX.Element} The section header row.
 */
export function SectionHeader({ icon, title, subtitle, actions }) {
    return (
        <div className={styles.sectionHeader}>
            <div>
                <h2 className={styles.sectionTitle}>{icon} {title}</h2>
                {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
            </div>
            {actions && <div className={styles.sectionActions}>{actions}</div>}
        </div>
    );
}


/**
 * Render previous and next pagination controls.
 * @param {string|null} previous Link presence marker for the previous page.
 * @param {string|null} next Link presence marker for the next page.
 * @param {Function} onPrevious Callback for the previous-page action.
 * @param {Function} onNext Callback for the next-page action.
 * @param {boolean} disabled Whether both controls should be temporarily disabled.
 * @param {Function} t Translation helper.
 * @returns {JSX.Element} Pagination control buttons.
 */
export function PaginationControls({ previous, next, onPrevious, onNext, disabled, t }) {
    return (
        <div className={styles.paginationRow}>
            <button className={styles.ghostButton} disabled={!previous || disabled} onClick={onPrevious} type="button">
                {t("catalogAdmin.previous")}
            </button>
            <button className={styles.ghostButton} disabled={!next || disabled} onClick={onNext} type="button">
                {t("catalogAdmin.next")}
            </button>
        </div>
    );
}
